import React from 'react';
import useGameStore from '../store/gameStore';
import { useTranslation } from '../i18n';
import { CHALLENGES } from '../data/challenges';
import { formatMoney } from '../utils/format';
import { Trophy, Clock, Check, X, Zap, Truck, Star, Crown, Target, Play, AlertTriangle, Building2, Globe, Users, Leaf, Landmark, Handshake } from 'lucide-react';

const CH_ICONS = { trophy: Trophy, truck: Truck, star: Star, crown: Crown, zap: Zap, building: Building2, globe: Globe, users: Users, leaf: Leaf, landmark: Landmark, handshake: Handshake };

function ProgressBar({ current, target, format }) {
  const pct = format === 'constraint' ? 100 : Math.min(100, (current / target) * 100);
  const isConstraint = format === 'constraint';
  const displayVal = format === 'money' ? formatMoney(current) : current;
  const displayTarget = format === 'money' ? formatMoney(target) : target;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px]">
        <span className="text-slate-500">{isConstraint ? `Actuel : ${displayVal} (max ${displayTarget})` : `${displayVal} / ${displayTarget}`}</span>
        <span className={`font-bold ${pct >= 100 && !isConstraint ? 'text-money' : isConstraint && current <= target ? 'text-money' : isConstraint ? 'text-danger' : 'text-slate-500'}`}>
          {isConstraint ? (current <= target ? 'OK' : 'ECHOUE') : `${Math.round(pct)}%`}
        </span>
      </div>
      <div className="h-2 rounded-full bg-ink-700/40 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            isConstraint ? (current <= target ? 'bg-money/100' : 'bg-danger/100') :
            pct >= 100 ? 'bg-money/100' : 'bg-accent'
          }`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

export default function Challenges() {
  const state = useGameStore();
  const { activeChallenge, completedChallenges, startChallenge, abandonChallenge, gameStarted, totalDays } = state;

  const activeCh = activeChallenge ? CHALLENGES.find(c => c.id === activeChallenge.id) : null;
  const elapsed = activeCh && activeChallenge ? totalDays - activeChallenge.startDay : 0;
  const remaining = activeCh ? Math.max(0, activeCh.timeLimitDays - elapsed) : 0;
  const { t } = useTranslation();

  return (
    <div className="space-y-4" data-testid="challenges-page">
      <h2 className="text-2xl font-bold text-foreground">{t('challenges_title')}</h2>

      {/* Active Challenge Banner */}
      {activeCh && (
        <div className={`rounded-xl p-5 bg-gradient-to-r ${activeCh.color} text-white shadow-lg`} data-testid="active-challenge">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6" />
              <div>
                <h3 className="font-bold text-lg">{activeCh.name}</h3>
                <p className="text-foreground/80 text-sm">{activeCh.description}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-foreground/90 text-sm">
                <Clock className="w-4 h-4" />
                <span className="font-bold">{remaining}j</span> restants
              </div>
              <p className="text-[10px] text-foreground/60">Jour {elapsed} / {activeCh.timeLimitDays}</p>
            </div>
          </div>

          {/* Timer bar */}
          <div className="h-1.5 rounded-full bg-ink-800/20 mb-4">
            <div
              className="h-full rounded-full bg-ink-800/70 transition-all"
              style={{ width: `${Math.min(100, (elapsed / activeCh.timeLimitDays) * 100)}%` }}
            />
          </div>

          {/* Objectives */}
          <div className="space-y-3">
            {activeCh.objectives.map(obj => {
              const current = obj.current(state);
              const met = obj.check(state);
              return (
                <div key={obj.id} className="bg-ink-800/10 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    {met && obj.format !== 'constraint' ? <Check className="w-4 h-4 text-foreground" /> :
                     obj.format === 'constraint' && !met ? <X className="w-4 h-4 text-red-300" /> :
                     <div className="w-4 h-4 rounded-full border-2 border-white/40" />}
                    <span className="text-sm font-medium">{obj.label}</span>
                  </div>
                  <ProgressBar current={current} target={obj.target} format={obj.format} />
                </div>
              );
            })}
          </div>

          <button
            onClick={abandonChallenge}
            className="mt-4 text-xs text-foreground/60 hover:text-foreground/90 transition-colors underline"
            data-testid="abandon-challenge"
          >
            {t('abandon_challenge')}
          </button>
        </div>
      )}

      {/* Challenge List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {CHALLENGES.map(ch => {
          const Icon = CH_ICONS[ch.icon] || Trophy;
          const isCompleted = !!(completedChallenges || {})[ch.id];
          const isActive = activeChallenge?.id === ch.id;
          const canStart = gameStarted && !activeChallenge && !isCompleted;

          return (
            <div
              key={ch.id}
              className={`bg-ink-800 rounded-xl border-2 overflow-hidden transition-all ${
                isCompleted ? 'border-money/60 bg-money/5' :
                isActive ? 'border-accent ring-2 ring-[#1F3864]/20' :
                'border-ink-600 hover:border-ink-500'
              }`}
              data-testid={`challenge-${ch.id}`}
            >
              <div className={`h-2 bg-gradient-to-r ${ch.color}`} />
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${ch.color} text-white`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-slate-100">{ch.name}</h4>
                      {isCompleted && <span className="text-[10px] px-2 py-0.5 rounded-full bg-money/100 text-white font-bold">{t('completed')}</span>}
                      {isActive && <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent text-white font-bold animate-pulse">{t('in_progress')}</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{ch.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                      <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" /> {ch.timeLimitDays}j</span>
                      <span className={`px-1.5 py-0.5 rounded ${
                        ch.difficulty === 'facile' ? 'bg-money/10 text-money' :
                        ch.difficulty === 'difficile' ? 'bg-danger/10 text-danger' :
                        'bg-accent/10 text-accent'
                      }`}>
                        {ch.difficulty === 'facile' ? 'Facile' : ch.difficulty === 'difficile' ? 'Difficile' : 'Normal'}
                      </span>
                    </div>

                    {/* Objectives preview */}
                    <div className="mt-2 space-y-1">
                      {ch.objectives.map(obj => (
                        <div key={obj.id} className="flex items-center gap-1.5 text-[10px] text-slate-500">
                          {isCompleted ? <Check className="w-3 h-3 text-money" /> :
                           isActive && obj.check(state) ? <Check className="w-3 h-3 text-money" /> :
                           isActive && obj.format === 'constraint' && !obj.check(state) ? <AlertTriangle className="w-3 h-3 text-danger" /> :
                           <div className="w-3 h-3 rounded-full border border-ink-500" />}
                          {obj.label}
                        </div>
                      ))}
                    </div>

                    <p className="text-[10px] text-warn mt-2 font-medium">{ch.reward}</p>
                  </div>
                </div>

                {canStart && (
                  <button
                    onClick={() => startChallenge(ch.id)}
                    className={`mt-3 w-full text-xs text-white py-2 rounded-lg font-bold bg-gradient-to-r ${ch.color} hover:opacity-90 transition-opacity flex items-center justify-center gap-1`}
                    data-testid={`start-challenge-${ch.id}`}
                  >
                    <Play className="w-3.5 h-3.5" /> {t('start_challenge')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
